import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Slider,
} from '@mui/material';
import { ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon } from '@mui/icons-material';

interface ProfileImageCropModalProps {
  open: boolean;
  onClose: () => void;
  imageFile: File | null;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspectRatio?: number;
}

const ProfileImageCropModal: React.FC<ProfileImageCropModalProps> = ({
  open,
  onClose,
  imageFile,
  onCropComplete,
  aspectRatio = 3/2,
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (imageFile && open) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
        setError(null);
      };
      reader.onerror = () => {
        setError('Failed to load image');
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile, open]);

  const getCroppedImg = async (image: HTMLImageElement): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Final output size based on aspect ratio
    const outputWidth = 600;
    const outputHeight = Math.round(600 / aspectRatio);
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Fill with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, outputWidth, outputHeight);

    // Get the container dimensions to match the preview
    const containerWidth = containerRef.current?.clientWidth || 500;
    const containerHeight = containerRef.current?.clientHeight || 400;
    const overlayWidth = 400;
    const overlayHeight = Math.round(400 / aspectRatio);

    // Calculate how the image is displayed in the preview
    const imageAspect = image.naturalWidth / image.naturalHeight;
    const containerAspect = containerWidth / containerHeight;
    
    let displayWidth, displayHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspect > containerAspect) {
      displayWidth = containerWidth;
      displayHeight = containerWidth / imageAspect;
      offsetY = (containerHeight - displayHeight) / 2;
    } else {
      displayHeight = containerHeight;
      displayWidth = containerHeight * imageAspect;
      offsetX = (containerWidth - displayWidth) / 2;
    }

    // Apply zoom
    const zoomedDisplayWidth = displayWidth * zoom;
    const zoomedDisplayHeight = displayHeight * zoom;

    // Calculate centers
    const containerCenterX = containerWidth / 2;
    const containerCenterY = containerHeight / 2;

    // Calculate the top-left corner of the zoomed image
    const imageLeft = containerCenterX - (zoomedDisplayWidth / 2) + pan.x;
    const imageTop = containerCenterY - (zoomedDisplayHeight / 2) + pan.y;

    // Calculate the crop area in image display coordinates
    const cropLeft = containerCenterX - (overlayWidth / 2) - imageLeft;
    const cropTop = containerCenterY - (overlayHeight / 2) - imageTop;
    const cropWidth = overlayWidth;
    const cropHeight = overlayHeight;

    // Convert from display coordinates to source image coordinates
    const scaleX = image.naturalWidth / zoomedDisplayWidth;
    const scaleY = image.naturalHeight / zoomedDisplayHeight;

    const sourceX = cropLeft * scaleX;
    const sourceY = cropTop * scaleY;
    const sourceWidth = cropWidth * scaleX;
    const sourceHeight = cropHeight * scaleY;

    // Ensure source rectangle is within image bounds
    const clampedSourceX = Math.max(0, Math.min(image.naturalWidth - 1, sourceX));
    const clampedSourceY = Math.max(0, Math.min(image.naturalHeight - 1, sourceY));
    const clampedSourceWidth = Math.min(sourceWidth, image.naturalWidth - clampedSourceX);
    const clampedSourceHeight = Math.min(sourceHeight, image.naturalHeight - clampedSourceY);

    // Ensure valid dimensions
    if (clampedSourceWidth <= 0 || clampedSourceHeight <= 0) {
      // Fallback: crop from center
      const centerX = image.naturalWidth / 2;
      const centerY = image.naturalHeight / 2;
      const size = Math.min(image.naturalWidth, image.naturalHeight);
      
      ctx.drawImage(
        image,
        centerX - size / 2,
        centerY - size / 2,
        size,
        size / aspectRatio,
        0,
        0,
        outputWidth,
        outputHeight
      );
    } else {
      ctx.drawImage(
        image,
        clampedSourceX,
        clampedSourceY,
        clampedSourceWidth,
        clampedSourceHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/jpeg', 0.95);
    });
  };

  const handleApply = async () => {
    if (!imgRef.current) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const croppedImageBlob = await getCroppedImg(imgRef.current);
      onCropComplete(croppedImageBlob);
      onClose();
    } catch (err) {
      console.error('Error cropping image:', err);
      setError('Failed to crop image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setImageSrc('');
    setError(null);
    onClose();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const newPan = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    const maxPan = 300 * zoom;
    newPan.x = Math.max(-maxPan, Math.min(maxPan, newPan.x));
    newPan.y = Math.max(-maxPan, Math.min(maxPan, newPan.y));

    setPan(newPan);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!imgRef.current) return;

    const step = 5;
    let newPan = { ...pan };

    switch (e.key) {
      case 'ArrowUp':
        newPan.y = Math.min(300 * zoom, pan.y + step);
        break;
      case 'ArrowDown':
        newPan.y = Math.max(-300 * zoom, pan.y - step);
        break;
      case 'ArrowLeft':
        newPan.x = Math.max(-300 * zoom, pan.x + step);
        break;
      case 'ArrowRight':
        newPan.x = Math.min(300 * zoom, pan.x - step);
        break;
      default:
        return;
    }

    e.preventDefault();
    setPan(newPan);
  };

  React.useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, pan, zoom]);

  React.useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [zoom]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Crop Image
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag to position, use arrow keys for precise movement, zoom to adjust.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {imageSrc && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              ref={containerRef}
              sx={{
                width: '100%',
                height: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
                position: 'relative',
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.2s ease-in-out',
                  pointerEvents: 'none'
                }}
                draggable={false}
              />
              {/* Rectangular crop overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '400px',
                  height: `${400 / aspectRatio}px`,
                  border: '3px dashed',
                  borderColor: 'primary.main',
                  borderRadius: '8px',
                  pointerEvents: 'none',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                }}
              />
            </Box>

            {/* Zoom Controls */}
            <Box sx={{ width: '100%', mb: 2 }}>
              <Typography variant="body2" gutterBottom sx={{ textAlign: 'center' }}>
                Zoom: {Math.round(zoom * 100)}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
                  disabled={zoom <= 0.25}
                >
                  <ZoomOutIcon />
                </Button>
                <Slider
                  value={zoom}
                  min={0.25}
                  max={3}
                  step={0.25}
                  onChange={(_, value) => setZoom(value as number)}
                  sx={{ flex: 1 }}
                />
                <Button
                  size="small"
                  onClick={() => setZoom(Math.min(3, zoom + 0.25))}
                  disabled={zoom >= 3}
                >
                  <ZoomInIcon />
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  size="small"
                  onClick={() => setZoom(1)}
                  variant="outlined"
                >
                  Reset Zoom
                </Button>
                <Button
                  size="small"
                  onClick={() => setPan({ x: 0, y: 0 })}
                  variant="outlined"
                >
                  Reset Position
                </Button>
              </Box>
            </Box>

            {/* Controls Info */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="caption" color="text.secondary">
                Keyboard: ↑↓←→ for precise positioning
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={loading || !imageSrc}
        >
          {loading ? 'Processing...' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileImageCropModal;
